import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  CircularProgress,
} from "@mui/material";
import { observer } from "mobx-react";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AppointmentDto,
  UpdateCancelledAppointmentDto,
  AppointmentQueryFilters,
  BatchCancelDeleteAppointmentDto,
  TimeSlotDto,
} from "../api-client";
import Controls from "../atoms/Controls";
import useStore from "../helpers/useStore";
import { AppointmentContext } from "../stores/Appointment/appointment.provider";
import notificationStore from "../stores/Notification/notificationStore";
import moment from "moment";
import { TransactionsContext } from "../stores/Transactions/Transactions.provider";
import { UserContext } from "../stores/User/User.provider";
import {
  recnungTransactionMethods,
  transactionTypes,
} from "../components/Transactions/Kasse/v2/types/kasse.types";
import {
  useBatchCancelAndDeleteAppointments,
  useBatchCancelAndDeleteTimeSlots,
  useSaveBlockedSlotForPatient,
} from "../stores/Appointment/appointment.mutation";
import { formatDateUI } from "../helpers/dateFormat";
import { useNoShowInvoiceTransaction } from "../stores/Transactions/transaction.mutations";
import { TherapySlotRow } from "./TherapySlotRow";
import { AppointmentTime } from "./AppointmentTime";

/**
 * Dense table cells (design-system: this is a data table, not a settings page).
 * `h-auto` on the head is required — shadcn's TableHead ships `h-12`, a 48px
 * header row, and the height does not merge away the way padding does.
 */
const CELL_BASE = "py-1 align-middle";
const HEAD_BASE =
  "h-auto py-1 text-left text-micro font-semibold uppercase tracking-wide text-ink-muted whitespace-nowrap";

const CELL = `${CELL_BASE} px-2`;
const HEAD = `${HEAD_BASE} px-2`;

/**
 * The four checkbox columns and the time column, narrower and with half the
 * side padding. 8px each side and a 64px floor per checkbox column pushed the
 * table past the width a ~1024px window leaves for it, and the last "Besuch"
 * header was the part that fell off the end. Nothing in these columns needs
 * the room: they hold a centred 14px checkbox and a time.
 */
const CELL_TIGHT = `${CELL_BASE} px-1`;
const HEAD_TIGHT = `${HEAD_BASE} w-14 px-1 text-center`;

/**
 * Width reserved for the table's pane, so that the centred pair does not move
 * when a scope is picked: the pane holds this width from the moment the dialog
 * opens, and the table lands inside it. Without the reservation the pane was
 * 0px wide until a scope was chosen and the whole layout jumped sideways as
 * the table appeared.
 *
 * Measured off the rendered table — date, time, a 240px therapy row and 56px
 * per checkbox column — plus room for the pane's own vertical scrollbar, which
 * is subtracted from this width and was itself covering the last column.
 * "Ohne Nachverfolgung" is the wide case: it adds the two Löschen columns.
 *
 * It is a reservation, not a cap. A table that needs more compresses first and
 * then scrolls the pane, which is visible — it is never quietly cut off.
 */
const TABLE_PANE_WIDTH = { withDelete: 680, cancelOnly: 560 };

/**
 * One label style for every group in the left column — the cancellation
 * context, the scope options, the filters. The column is a control panel, not
 * a dense data surface, so it does not use the 9–11.5px table scale: at 12.5px
 * uppercase it sits level with the MUI field labels underneath it, and its
 * content reads at 14px rather than the 11.5px that made the whole column look
 * like a footnote.
 *
 * Deliberately NOT `data-density="compact"`, which the design system offers for
 * narrow panels inside a wide window: this panel holds the decisions the dialog
 * is about, and it was already being read as too small to be one of them.
 */
const SIDEBAR_LABEL =
  "text-title font-semibold uppercase tracking-wide text-ink-muted";

/** A group under such a label. The rule above it separates it from the one
 *  before without spending a heading on the distinction. */
const SIDEBAR_SECTION =
  "border-0 border-t border-solid border-ink-line-faint pt-3";

const SCOPE_OPTIONS = [
  { id: "single", label: "Nur diesen Termin" },
  { id: "rx", label: "Alle Termine mit diesem Rezept" },
  { id: "future", label: "Alle zukünftigen Termine" },
  { id: "select", label: "Termine manuell auswählen" },
];

const WHO_CANCELLED_LABEL: Record<string, string> = {
  patient: "Patient",
  therapist: "Therapeut",
  no_trace: "Ohne Nachverfolgung",
};

interface BatchCancelDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: number;
  cancelAppDto: UpdateCancelledAppointmentDto;
  appointment: AppointmentDto;
  accountSelected: string | null;
  whoCancelled?: "patient" | "therapist" | "no_trace";
  unavailabiltyRange?: { startDate: Date; endDate: Date };
}

const BatchCancelAppointmentDialog: React.FC<BatchCancelDialogProps> = observer(
  ({
    open,
    onClose,
    patientId,
    cancelAppDto,
    appointment,
    whoCancelled,
    unavailabiltyRange,
    accountSelected,
  }) => {
    const AppointmentStore = useStore(AppointmentContext);
    const { getAppointmentsForPatient, getFilteredAppointmentsForPatient } =
      AppointmentStore;
    const { mutate: saveBlockedSlotForPatient } =
      useSaveBlockedSlotForPatient();
    const rxIdsRef = useRef<string[]>([]);
    const { mutateAsync: batchCancelAndDeleteAppointments } =
      useBatchCancelAndDeleteAppointments();
    const { mutateAsync: batchCancelAndDeleteTimeSlots } =
      useBatchCancelAndDeleteTimeSlots();
    const { mutateAsync: noShowInvoiceTransaction } =
      useNoShowInvoiceTransaction();
    const UserStore = useStore(UserContext);
    const { user } = UserStore;

    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [appointments, setAppointments] = useState<AppointmentDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [appointmentsToDelete, setAppointmentsToDelete] = useState<string[]>(
      [],
    );
    const [timeSlotsToCancel, setTimeSlotsToCancel] = useState<string[]>([]);
    const [timeSlotsToDelete, setTimeSlotsToDelete] = useState<string[]>([]);
    const [filterRx, setFilterRx] = useState<string>("");
    const [filterSeries, setFilterSeries] = useState<string>("");
    const [filterTreatment, setFilterTreatment] = useState<string>("");
    // apply filters
    const filteredAppointments = appointments.filter((a) => {
      const slots = a.timeSlots ?? [];
      if (!filterRx && !filterSeries && !filterTreatment) return true; // no filters → include all
      return slots.some(
        (ts) =>
          (!filterRx || ts?.therapyRx?.rx?.rxNumber === filterRx) &&
          (!filterSeries ||
            (ts?.therapyRx?.rx?.rxSeries as any)?.label === filterSeries) &&
          (!filterTreatment ||
            (ts?.therapy as any)?.abbreviation === filterTreatment),
      );
    });

    const doesSlotMatchFilters = (ts?: TimeSlotDto) => {
      if (!filterRx && !filterSeries && !filterTreatment && !selectedOption)
        return true;
      // if (selectedOption === "rx" && rxIdsRef.current?.length) {
      //   const rxId = ts?.therapyRx?.rx?.id;
      //   if (!rxId) return false; // ignore undefined
      //   return rxIdsRef.current.includes(rxId);
      // }
      // Check Rx option first
      if (selectedOption === "rx" && rxIdsRef.current?.length) {
        const rxId = ts?.therapyRx?.rx?.id;
        if (!rxId) return false;
        // include if in rxIdsRef AND matches other filters
        return (
          rxIdsRef.current.includes(rxId) &&
          (!filterRx || ts?.therapyRx?.rx?.rxNumber === filterRx) &&
          (!filterSeries ||
            (ts?.therapyRx?.rx?.rxSeries as any)?.label === filterSeries) &&
          (!filterTreatment ||
            (ts?.therapy as any)?.abbreviation === filterTreatment)
        );
      }
      return (
        (!filterRx || ts?.therapyRx?.rx?.rxNumber === filterRx) &&
        (!filterSeries ||
          (ts?.therapyRx?.rx?.rxSeries as any)?.label === filterSeries) &&
        (!filterTreatment ||
          (ts?.therapy as any)?.abbreviation === filterTreatment)
      );
    };

    const handleSelectAllDelete = (checked: boolean) => {
      if (!checked) {
        setAppointmentsToDelete([]);
        setTimeSlotsToDelete([]);
        return;
      }

      const nextAppointmentsToDelete: string[] = [];
      const nextTimeSlotsToDelete: string[] = [];

      appointments.forEach((appt) => {
        const slots = appt.timeSlots ?? [];
        const matchingSlots = slots.filter(doesSlotMatchFilters);

        if (matchingSlots.length === 0) {
          // No matching slots → ignore appointment
          return;
        }

        if (matchingSlots.length === slots.length) {
          // All slots match → appointment-level delete
          nextAppointmentsToDelete.push(appt.id!);
        } else {
          // Partial match → slot-level delete
          matchingSlots.forEach((slot) => {
            if (slot.id) nextTimeSlotsToDelete.push(slot.id);
          });
        }
      });

      setAppointmentsToDelete(nextAppointmentsToDelete);
      setTimeSlotsToDelete(nextTimeSlotsToDelete);

      // Clear cancel selections
      setAppointmentsToCancel([]);
      setTimeSlotsToCancel([]);
    };

    const handleSelectAllCancel = (checked: boolean) => {
      if (!checked) {
        setAppointmentsToCancel([]);
        setTimeSlotsToCancel([]);
        return;
      }

      const nextAppointmentsToCancel: string[] = [];
      const nextTimeSlotsToCancel: string[] = [];

      appointments.forEach((appt) => {
        const slots = appt.timeSlots ?? [];
        const matchingSlots = slots.filter(doesSlotMatchFilters);

        if (matchingSlots.length === 0) {
          // No matching slots → ignore appointment
          return;
        }

        if (matchingSlots.length === slots.length) {
          // All slots match → appointment-level cancel
          nextAppointmentsToCancel.push(appt.id!);
        } else {
          // Partial match → slot-level cancel
          matchingSlots.forEach((slot) => {
            if (slot.id) nextTimeSlotsToCancel.push(slot.id);
          });
        }
      });

      setAppointmentsToCancel(nextAppointmentsToCancel);
      setTimeSlotsToCancel(nextTimeSlotsToCancel);

      // Clear delete selections
      setAppointmentsToDelete([]);
      setTimeSlotsToDelete([]);
    };

    const [appointmentsToCancel, setAppointmentsToCancel] = useState<string[]>(
      [],
    );
    /**
     * The scope options that name their own set ("nur diesen Termin", "alle
     * Termine mit diesem Rezept", "alle zukünftigen Termine") pre-select
     * everything they loaded — that set IS the answer, and unchecking it would
     * be undoing the option just chosen. "Termine manuell auswählen" is the
     * opposite: the user picked it to make the choice themselves, so it starts
     * empty and they check what they mean.
     *
     * Pre-selecting through handleSelectAllCancel rather than marking every
     * appointment wholesale: under "Rezept" an appointment can also carry slots
     * of another Rx (a Doppel across two Rezepte), and only the slots that
     * belong to the chosen Rx may come pre-checked.
     */
    useEffect(() => {
      if (selectedOption === "select") return;
      if (appointments.length > 0 && appointmentsToCancel.length === 0) {
        handleSelectAllCancel(true);
      }
    }, [appointments]);

    const handleTimeslotToggle = (
      apptId: string,
      slotId: string,
      action: "cancel" | "delete",
      checked: boolean,
    ) => {
      // Always clear parent appointment when toggling a single timeslot
      setAppointmentsToCancel((prev) => prev.filter((id) => id !== apptId));
      setAppointmentsToDelete((prev) => prev.filter((id) => id !== apptId));

      if (action === "cancel") {
        setTimeSlotsToCancel((prev) => {
          const updated = checked
            ? [...prev, slotId]
            : prev.filter((id) => id !== slotId);

          // Find appointment's total timeslots
          const appt = appointments.find((a) => a.id === apptId);
          const allSlotIds = appt?.timeSlots?.map((ts) => ts.id!) || [];

          // If ALL timeslots are selected → move to appointment-level
          if (
            allSlotIds.length > 0 &&
            allSlotIds.every((id) => updated.includes(id))
          ) {
            setAppointmentsToCancel((prevApps) => [...prevApps, apptId]);
            return updated.filter((id) => !allSlotIds.includes(id)); // clear timeslot-level
          }

          return updated;
        });

        // Ensure delete list stays clean
        setTimeSlotsToDelete((prev) => prev.filter((id) => id !== slotId));
      }

      if (action === "delete") {
        setTimeSlotsToDelete((prev) => {
          const updated = checked
            ? [...prev, slotId]
            : prev.filter((id) => id !== slotId);

          const appt = appointments.find((a) => a.id === apptId);
          const allSlotIds = appt?.timeSlots?.map((ts) => ts.id!) || [];

          if (
            allSlotIds.length > 0 &&
            allSlotIds.every((id) => updated.includes(id))
          ) {
            setAppointmentsToDelete((prevApps) => [...prevApps, apptId]);
            return updated.filter((id) => !allSlotIds.includes(id));
          }

          return updated;
        });

        setTimeSlotsToCancel((prev) => prev.filter((id) => id !== slotId));
      }
    };

    // ---------------------- FILTERS ----------------------

    // derive unique lists from appointments
    const uniqueRxNumbers = Array.from(
      new Set(
        (appointments ?? [])
          .flatMap((a) => a.timeSlots ?? [])
          .map((ts) => ts?.therapyRx?.rx?.rxNumber)
          .filter((v) => typeof v === "string" && v.trim() !== ""),
      ),
    );

    const uniqueSeriesNames = Array.from(
      new Set(
        (appointments ?? [])
          .flatMap((a) => a.timeSlots ?? [])
          .map((ts) => (ts?.therapyRx?.rx?.rxSeries as any)?.label)
          .filter((v) => typeof v === "string" && v.trim() !== ""),
      ),
    );

    const uniqueTreatmentNames = Array.from(
      new Set(
        (appointments ?? [])
          .flatMap((a) => a.timeSlots ?? [])
          .map((ts) => (ts?.therapy as any)?.abbreviation)
          .filter((v) => typeof v === "string" && v.trim() !== ""),
      ),
    );

    const handleAppointmentToggle = (
      appt: AppointmentDto,
      action: "cancel" | "delete",
      checked: boolean,
    ) => {
      if (action === "cancel") {
        if (checked) {
          setAppointmentsToCancel((prev) => [...prev, appt.id!]);
          // clear its timeslots
          setTimeSlotsToCancel((prev) =>
            prev.filter(
              (id) => !appt.timeSlots?.some((ts: any) => ts.id === id),
            ),
          );
          setTimeSlotsToDelete((prev) =>
            prev.filter(
              (id) => !appt.timeSlots?.some((ts: any) => ts.id === id),
            ),
          );
        } else {
          setAppointmentsToCancel((prev) =>
            prev.filter((id) => id !== appt.id),
          );
        }
      }

      if (action === "delete") {
        if (checked) {
          setAppointmentsToDelete((prev) => [...prev, appt.id!]);
          // clear its timeslots
          setTimeSlotsToCancel((prev) =>
            prev.filter(
              (id) => !appt.timeSlots?.some((ts: any) => ts.id === id),
            ),
          );
          setTimeSlotsToDelete((prev) =>
            prev.filter(
              (id) => !appt.timeSlots?.some((ts: any) => ts.id === id),
            ),
          );
        } else {
          setAppointmentsToDelete((prev) =>
            prev.filter((id) => id !== appt.id),
          );
        }
      }
    };

    const initialFilters: AppointmentQueryFilters = {
      showOnlyFutureAppointments: false,
      showOnlyActiveRxs: false,
      showLastThreeMonths: false,
      rxIds: [],
    };

    const handleSelection = async (option: string) => {
      setSelectedOption(option);
      setAppointments([]);
      setAppointmentsToCancel([]);
      setAppointmentsToDelete([]);
      setTimeSlotsToCancel([]);
      setTimeSlotsToDelete([]);
      setLoading(true);

      try {
        let data: AppointmentDto[] = [];
        if (option === "single") {
          data = [appointment];
        } else if (option === "rx") {
          const rxIds: string[] =
            appointment?.timeSlots
              ?.map((t: any) => t.therapyRx?.rx?.id)
              .filter(Boolean) ?? [];
          rxIdsRef.current = rxIds;
          const allAppointments = await getFilteredAppointmentsForPatient(
            patientId,
            { ...initialFilters },
            true,
          );

          // Filter appointments that have at least one time slot with a matching RX
          data =
            allAppointments?.filter((appt: any) =>
              appt.timeSlots?.some((ts: any) =>
                rxIds.includes(ts.therapyRx?.rx?.id),
              ),
            ) ?? []; // <- ensures data is always an array
        } else if (option === "future") {
          data = (await getFilteredAppointmentsForPatient(
            patientId,
            {
              ...initialFilters,
              showOnlyFutureAppointments: true,
              start: appointment?.start,
            },
            true,
          )) as any;
        } else if (option === "select") {
          data = (await getAppointmentsForPatient(
            patientId as unknown as string,
          )) as any;
          data = data.filter((appt) => appt.start && appt.end);
        }
        setAppointments(data);
      } catch (err) {
        console.error("Error fetching appointments:", err);
      } finally {
        setLoading(false);
      }
    };
    // console.log(
    // "AOSOLSLSL:",
    // appointmentsToCancel,
    // appointmentsToDelete,
    // timeSlotsToCancel,
    // timeSlotsToDelete,
    // );
    const handleBatchCancelDeleteClick = async () => {
      try {
        setSaveLoading(true);

        // Validation for cancel reason
        if (
          (whoCancelled === "patient" || whoCancelled === "therapist") &&
          !cancelAppDto?.reason &&
          appointmentsToCancel.length > 0
        ) {
          notificationStore.showMessage(
            "Der Grund für die Stornierung ist zwingend erforderlich!!!",
            "warning",
          );
          return;
        }

        if (
          whoCancelled === "patient" &&
          !accountSelected &&
          cancelAppDto.noShowInvoice &&
          appointmentsToCancel.length > 0
        ) {
          notificationStore.showMessage(
            "Bitte wählen Sie ein Mandant aus.",
            "warning",
          );
          return;
        }

        const promises: any = [];

        // Call appointments API if IDs exist
        if (
          appointmentsToCancel.length > 0 ||
          appointmentsToDelete.length > 0
        ) {
          const appointmentDto: BatchCancelDeleteAppointmentDto = {
            cancelIds: appointmentsToCancel,
            deleteIds: appointmentsToDelete,
            whoCancelled: whoCancelled ?? "",
            cancelAppDto,
          };
          promises.push(
            batchCancelAndDeleteAppointments({
              batchCancelAndDeleteAppointments: appointmentDto,
              patientId,
            }),
          );
        }

        // Call timeslots API if IDs exist
        if (timeSlotsToCancel.length > 0 || timeSlotsToDelete.length > 0) {
          const timeSlotDto: BatchCancelDeleteAppointmentDto = {
            cancelTimeSlotIds: timeSlotsToCancel,
            deleteTimeSlotIds: timeSlotsToDelete,
            whoCancelled: whoCancelled ?? "",
            cancelAppDto,
          };
          promises.push(
            batchCancelAndDeleteTimeSlots({
              batchCancelAndDeleteTimeSlots: timeSlotDto,
              patientId,
            }),
          );
        }

        // Execute APIs sequentially or parallel, await results
        const results = await Promise.all(promises);

        const resultAppointments = results[0]?.cancelled?.data || [];
        const resultTimeSlotsData = results[1]?.cancelled?.data || [];

        // Save unavailability if patient cancelled
        if (
          whoCancelled === "patient" &&
          unavailabiltyRange &&
          (resultAppointments.length > 0 || resultTimeSlotsData.length > 0)
        ) {
          const timeSlot: TimeSlotDto = {
            start: new Date(unavailabiltyRange.startDate).toISOString(),
            end: new Date(unavailabiltyRange.endDate).toISOString(),
            type: "PatientUnvailable",
          };
          saveBlockedSlotForPatient({ patientId, blockedSlots: [timeSlot] });
        }

        // Process no-show invoices if required
        if (cancelAppDto.noShowInvoice) {
          const cancelledData = [...resultAppointments, ...resultTimeSlotsData];

          for (const cancelledAppData of cancelledData) {
            if (!cancelledAppData) continue;

            const tarifGroupIds =
              cancelledAppData.cancelledAppt.therapyRxs?.map(
                (therapyRx: any) => therapyRx.rx?.tarifGroup?.id,
              ) || [];

            const allSameTarifGroup = tarifGroupIds.every(
              (id: any) => id === tarifGroupIds[0],
            );
            if (!allSameTarifGroup) {
              throw new Error("Die Tarifgruppen-IDs sind unterschiedlich.");
            }

            const amount = Number(cancelledAppData.amount.toFixed(2));

            const transactionData = {
              text: `Ausgefallener Termin am ${moment(
                cancelledAppData.cancelledAppt.createdAt,
              ).format("DD.MM.YYYY")}`,
              type: transactionTypes.EINNAHME,
              amount,
              method: recnungTransactionMethods.RECHNUNG,
              accountId: accountSelected!,
              userId: user?.id || "",
              wasExported: false,
              patientId: patientId,
              textDescription: "Ausfallrechnung",
              tAccountId: tarifGroupIds[0] === "Privat" ? 4105 : 4101,
            };

            const data = await noShowInvoiceTransaction({
              appointmentId: cancelledAppData.cancelledAppt.appointment?.id,
              rechnungDto: transactionData,
              patientId: patientId,
            });

            if (data?.docDto?.accessUrl) {
              window.open(data.docDto.accessUrl, "_blank");
            }
          }
        }

        notificationStore.showMessage(
          "Termine erfolgreich verarbeitet.",
          "success",
        );
      } catch (err: any) {
        notificationStore.showMessage(
          err?.message || "Fehler bei der Verarbeitung",
          "error",
        );
        console.error("Error in batch cancel/delete:", err);
      } finally {
        setSaveLoading(false);
        onClose();
      }
    };

    const handleClose = () => {
      setSelectedOption(null);
      setAppointments([]);
      setAppointmentsToCancel([]);
      setAppointmentsToDelete([]);
      setTimeSlotsToCancel([]);
      setTimeSlotsToDelete([]);
      onClose();
    };
    useEffect(() => {
      const filteredIds = filteredAppointments.map((a) => a.id!);
      const filteredSlotIds = filteredAppointments.flatMap(
        (a) => a.timeSlots?.map((ts) => ts.id!) || [],
      );

      setAppointmentsToCancel((prev) => {
        const next = prev.filter((id) => filteredIds.includes(id));
        return next.length === prev.length ? prev : next;
      });

      setAppointmentsToDelete((prev) => {
        const next = prev.filter((id) => filteredIds.includes(id));
        return next.length === prev.length ? prev : next;
      });

      setTimeSlotsToCancel((prev) => {
        const next = prev.filter((id) => filteredSlotIds.includes(id));
        return next.length === prev.length ? prev : next;
      });

      setTimeSlotsToDelete((prev) => {
        const next = prev.filter((id) => filteredSlotIds.includes(id));
        return next.length === prev.length ? prev : next;
      });
    }, [filteredAppointments]);

    /**
     * A filter narrows the scope, it does not replace it: re-apply the chosen
     * option's pre-selection to what is left, so "alle Termine mit diesem
     * Rezept" plus a Behandlung filter still arrives pre-checked. Under
     * "manuell auswählen" there is nothing to re-apply, so the filter just
     * clears.
     */
    useEffect(() => {
      if (appointments.length > 0) {
        handleSelectAllCancel(
          selectedOption !== null && selectedOption !== "select",
        );
        handleSelectAllDelete(false);
      }
    }, [filterRx, filterSeries, filterTreatment]);

    /**
     * Every appointment fully covered — either selected as a whole or with all
     * of its filter-matching slots selected. Drives the group select-all in the
     * header.
     */
    const allSelected = (
      selectedAppointmentIds: string[],
      selectedSlotIds: string[],
    ) =>
      appointments.length > 0 &&
      appointments.every((appt) => {
        const matching = (appt.timeSlots ?? []).filter(doesSlotMatchFilters);
        return (
          matching.length > 0 &&
          (selectedAppointmentIds.includes(appt.id!) ||
            matching.every((slot) => selectedSlotIds.includes(slot.id!)))
        );
      });

    const allCancelSelected = allSelected(
      appointmentsToCancel,
      timeSlotsToCancel,
    );
    const allDeleteSelected = allSelected(
      appointmentsToDelete,
      timeSlotsToDelete,
    );

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        // Same width and fixed geometry as the dialog that opens this one — it
        // opens on top of it, so a narrower paper reads as the window jumping.
        // Picking a scope also loads a table, which without a height would
        // resize the paper on every selection.
        maxWidth="xl"
        sx={{ "& .MuiDialog-paper": { height: "min(760px, 92vh)" } }}
      >
        <DialogTitle>Mehrere Termine stornieren</DialogTitle>
        {/*
          The content box itself must not scroll: the two panes below scroll
          independently, and a scrollbar here would carry the controls out of
          view as soon as the user worked down the table.
        */}
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
        >
          {/*
            Controls left, table right. The controls are a handful of short
            rows — laid out across the full width of an `xl` paper they were
            three dropdowns of 400px each and a radio row with metres between
            the options, and the table got whatever was left underneath. In a
            column they read as one stack of decisions, top to bottom, and the
            table gets the space it actually needs.

            Neither pane takes the slack: the column is a fixed 260px and the
            table's pane holds a reserved width (TABLE_PANE_WIDTH), so the pair
            is centred in the paper instead of sitting against its left edge
            with the leftover width pooling on the right. The reservation is
            what makes centring hold still — sizing that pane to its contents
            meant it was 0px wide until a scope was picked, and the whole layout
            slid sideways the moment the table appeared.

            `pt-4`: MUI zeroes a DialogContent's top padding when it follows a
            DialogTitle (`.MuiDialogTitle-root + &`), which is what had the
            first label sitting up against the title. The padding goes here
            rather than in `sx`, where that two-class selector would outrank it.
          */}
          <div className="flex min-h-0 flex-1 justify-center gap-4 pt-4">
            <div className="flex w-[260px] shrink-0 flex-col gap-3 overflow-y-auto pr-2">
              {/* Label above value, not beside it: in a column this narrow
                  "Storniert durch" and "Ohne Nachverfolgung" do not fit on one
                  line together. */}
              <div className="flex flex-col gap-2">
                <div>
                  <div className={SIDEBAR_LABEL}>Storniert durch</div>
                  <div className="text-lead font-semibold text-ink-text">
                    {WHO_CANCELLED_LABEL[whoCancelled ?? ""] ?? "—"}
                  </div>
                </div>
                {cancelAppDto?.reason && (
                  <div>
                    <div className={SIDEBAR_LABEL}>Grund</div>
                    <div className="text-lead text-ink-text">
                      {cancelAppDto.reason}
                    </div>
                  </div>
                )}
                {/* Only when there is one — an empty "Mandant:" was a label
                    standing in for nothing. */}
                {whoCancelled === "patient" && accountSelected && (
                  <div>
                    <div className={SIDEBAR_LABEL}>Mandant</div>
                    <div className="text-lead text-ink-text">
                      {accountSelected}
                    </div>
                  </div>
                )}
              </div>

              {/*
                "Termine auswählen", not "Wie möchtest du die Termine
                auswählen?": as a full-width question it was the one line in the
                column styled as a heading, and at 12.5px semibold it read as
                just another value rather than as the thing that labels the four
                options. Every group in this column now carries the same kind of
                label, and the options say plainly enough what they select.
              */}
              <div className={SIDEBAR_SECTION}>
                <div className={SIDEBAR_LABEL}>Termine auswählen</div>

                {/*
                  `htmlFor` against an id on the item, rather than wrapping the
                  item in the label: a shadcn RadioGroupItem renders a <button>,
                  and the implicit association of a wrapping <label> with a
                  button is the kind of thing that works until it doesn't. The
                  explicit pairing is what Radix documents.

                  The row — circle and text — is one hit target, `py-1` tall, so
                  clicking near the text lands on the label rather than in the
                  gap between two options.
                */}
                <RadioGroup
                  value={selectedOption ?? ""}
                  onValueChange={(value) => handleSelection(value)}
                  className="mt-1.5 flex flex-col gap-0.5"
                >
                  {SCOPE_OPTIONS.map((option) => (
                    <div key={option.id} className="flex items-center">
                      <RadioGroupItem
                        id={`scope-${option.id}`}
                        value={option.id}
                        className="h-4 w-4 shrink-0"
                      />
                      {/* The spacing is padding on the label, not a gap on the
                          row: a gap is dead space between two hit targets. */}
                      <label
                        htmlFor={`scope-${option.id}`}
                        className="flex-1 cursor-pointer py-1 pl-2 text-lead text-ink-text"
                      >
                        {option.label}
                      </label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* ---------------------- FILTER UI ---------------------- */}
              {/*
                A plain flex column, not `<Grid container spacing={2}>`. That
                Grid was also the reason the radio buttons above it only
                sometimes took a click: MUI gives a spaced container
                `margin-top: -16px` and its items a matching `padding-top`, so a
                16px band of transparent Grid item sat on top of the radio row
                and swallowed the hover and the click wherever it overlapped.
              */}
              {selectedOption && (
                <div className={SIDEBAR_SECTION}>
                  <div className={SIDEBAR_LABEL}>Filter</div>
                  <div className="mt-1 flex flex-col gap-2">
                    <Controls.Select
                      label="Rezeptnummer"
                      name="rxFilter"
                      value={filterRx}
                      onChange={(e: any) => setFilterRx(e.target.value)}
                      options={uniqueRxNumbers.map((x) => ({
                        value: x,
                        label: x,
                      }))}
                    />

                    <Controls.Select
                      label="Serie"
                      name="seriesFilter"
                      value={filterSeries}
                      onChange={(e: any) => setFilterSeries(e.target.value)}
                      options={uniqueSeriesNames.map((x) => ({
                        value: x,
                        label: x,
                      }))}
                    />

                    <Controls.Select
                      label="Behandlung"
                      name="treatmentFilter"
                      value={filterTreatment}
                      onChange={(e: any) => setFilterTreatment(e.target.value)}
                      options={uniqueTreatmentNames.map((x) => ({
                        value: x,
                        label: x,
                      }))}
                    />
                  </div>
                </div>
              )}
            </div>

            {/*
              The scrolling pane. `w-fit` on the card because the table is a
              date and four checkbox columns — a few hundred pixels of content
              — and stretched to the pane it would spread those columns apart
              again, which is the layout everyone reads as broken.

              Two things cut the last "Besuch" header off before: a `max-w-full`
              on the card, which shrank the card while the table inside kept its
              width and left the overflow trapped in the Table component's own
              `overflow-auto` — a horizontal scrollbar at the foot of a table
              taller than the dialog, i.e. nowhere anyone will see it — and this
              pane's vertical scrollbar, which is subtracted from the width
              below and took its 15px straight off the last column. The
              reservation covers the scrollbar; the card is no longer capped, so
              any overflow that remains belongs to this pane and shows.
            */}
            <div
              className="min-w-0 shrink overflow-auto"
              style={{
                width:
                  whoCancelled === "no_trace"
                    ? TABLE_PANE_WIDTH.withDelete
                    : TABLE_PANE_WIDTH.cancelOnly,
              }}
            >
              {selectedOption && (
                /* `min-w-full` fills the pane's reserved width, so the card
                   is the same size whether it holds a spinner, an empty-state
                   line or the table. `w-fit` rather than `w-full` so a table
                   that needs more than the reservation can still take it. */
                <div className="w-fit min-w-full rounded-card border border-solid border-ink-edge bg-card">
                  {loading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 4 }}
                    >
                      <CircularProgress size={28} />
                    </Box>
                  ) : filteredAppointments &&
                    filteredAppointments.length > 0 ? (
                    /*
                  Font size inline rather than `className="text-dense"`:
                  shadcn's Table sets `text-sm` and merges through `cn()` →
                  tailwind-merge, which does not know our custom text steps, so
                  both classes survive and `text-sm` wins on output order.
                */
                    <Table
                      style={{
                        fontSize: "var(--text-dense)",
                        lineHeight: 1.25,
                      }}
                    >
                      <TableHeader>
                        <TableRow className="border-0 hover:bg-transparent">
                          <TableHead rowSpan={2} colSpan={3} className={HEAD}>
                            Termin
                          </TableHead>

                          {/* CANCEL COLUMNS — one select-all for the group. The two
                          sub-columns used to carry a checkbox each, both wired
                          to the same handler and the same checked expression:
                          two controls that did the identical thing. */}
                          <TableHead
                            colSpan={2}
                            className={`${HEAD} text-center`}
                          >
                            <span className="inline-flex items-center gap-1.5">
                              <Checkbox
                                checked={allCancelSelected}
                                onCheckedChange={(value) =>
                                  handleSelectAllCancel(value === true)
                                }
                                className="h-3.5 w-3.5"
                                aria-label="Alle stornieren"
                              />
                              Stornieren
                            </span>
                          </TableHead>

                          {/* DELETE COLUMNS */}
                          {whoCancelled === "no_trace" && (
                            <TableHead
                              colSpan={2}
                              className={`${HEAD} text-center`}
                            >
                              <span className="inline-flex items-center gap-1.5">
                                <Checkbox
                                  checked={allDeleteSelected}
                                  onCheckedChange={(value) =>
                                    handleSelectAllDelete(value === true)
                                  }
                                  className="h-3.5 w-3.5"
                                  aria-label="Alle löschen"
                                />
                                Löschen
                              </span>
                            </TableHead>
                          )}
                        </TableRow>

                        <TableRow className="border-0 border-b border-solid border-ink-line-strong hover:bg-transparent">
                          <TableHead className={HEAD_TIGHT}>Einheit</TableHead>
                          <TableHead className={HEAD_TIGHT}>Besuch</TableHead>
                          {whoCancelled === "no_trace" && (
                            <>
                              <TableHead className={HEAD_TIGHT}>
                                Einheit
                              </TableHead>
                              <TableHead className={HEAD_TIGHT}>
                                Besuch
                              </TableHead>
                            </>
                          )}
                        </TableRow>
                      </TableHeader>

                      <TableBody>
                        {appointments.map((appt, apptIndex) => {
                          const isCancelSelected =
                            appointmentsToCancel.includes(appt.id!);
                          const isDeleteSelected =
                            appointmentsToDelete.includes(appt.id!);
                          const timeslots = appt.timeSlots || [];

                          return (
                            <React.Fragment key={appt.id}>
                              {timeslots.map((slot, idx) => {
                                const isSlotCancel = timeSlotsToCancel.includes(
                                  slot.id!,
                                );
                                const isSlotDelete = timeSlotsToDelete.includes(
                                  slot.id!,
                                );
                                const slotMatches = doesSlotMatchFilters(slot);

                                return (
                                  <TableRow
                                    key={slot.id}
                                    /* One appointment is one block of rows: a rule
                                   above each new appointment separates them,
                                   which is what the 16px grey spacer row was
                                   for. Slots within an appointment get the
                                   faint rule. */
                                    className={[
                                      "border-0 border-b border-solid border-ink-line-faint hover:bg-ink-band",
                                      idx === 0 && apptIndex > 0
                                        ? "border-t border-t-ink-line-strong"
                                        : "",
                                      slotMatches
                                        ? ""
                                        : "pointer-events-none opacity-40",
                                    ].join(" ")}
                                  >
                                    {/* Date once per appointment, not once per slot. */}
                                    {idx === 0 && (
                                      <TableCell
                                        rowSpan={timeslots.length}
                                        className={`${CELL} whitespace-nowrap align-top text-dense font-semibold tabular-nums text-ink-text`}
                                      >
                                        {appt.start
                                          ? formatDateUI(appt.start)
                                          : "–"}
                                      </TableCell>
                                    )}

                                    <TableCell
                                      className={`${CELL_TIGHT} whitespace-nowrap text-dense tabular-nums text-ink-text`}
                                    >
                                      <AppointmentTime
                                        start={slot.start}
                                        end={slot.end}
                                        appointmentStart={appt.start}
                                        appointmentEnd={appt.end}
                                      />
                                    </TableCell>

                                    {/* The same slot rendering as the Termin card and
                                    the calendar: Rx number as a coloured rail
                                    rather than a grey pill, therapy as text. */}
                                    <TableCell className={CELL}>
                                      {/* Bounded: the row's therapy column is
                                      `flex-1`, so in a table cell that takes
                                      all the slack it would stretch the status
                                      symbols to the far edge of the dialog and
                                      leave a hole in the middle of every row. */}
                                      <div className="w-[240px] max-w-full">
                                        <TherapySlotRow
                                          timeSlot={slot}
                                          appointment={appt}
                                          index={idx}
                                          totalSlots={timeslots.length}
                                        />
                                      </div>
                                    </TableCell>

                                    {/* Cancel Einheit */}
                                    <TableCell className={CELL_TIGHT}>
                                      <div className="flex justify-center">
                                        <Checkbox
                                          checked={isSlotCancel}
                                          onCheckedChange={(value) =>
                                            handleTimeslotToggle(
                                              appt.id!,
                                              slot.id!,
                                              "cancel",
                                              value === true,
                                            )
                                          }
                                          disabled={
                                            isCancelSelected || isDeleteSelected
                                          }
                                          className="h-3.5 w-3.5"
                                          aria-label="Einheit stornieren"
                                        />
                                      </div>
                                    </TableCell>

                                    {/* Cancel Besuch (whole appointment) */}
                                    {idx === 0 && (
                                      <TableCell
                                        rowSpan={timeslots.length}
                                        className={`${CELL_TIGHT} align-top`}
                                      >
                                        <div className="flex justify-center">
                                          <Checkbox
                                            checked={isCancelSelected}
                                            onCheckedChange={(value) =>
                                              handleAppointmentToggle(
                                                appt,
                                                "cancel",
                                                value === true,
                                              )
                                            }
                                            className="h-3.5 w-3.5"
                                            aria-label="Besuch stornieren"
                                          />
                                        </div>
                                      </TableCell>
                                    )}

                                    {/* Delete Einheit */}
                                    {whoCancelled === "no_trace" && (
                                      <TableCell className={CELL_TIGHT}>
                                        <div className="flex justify-center">
                                          <Checkbox
                                            checked={isSlotDelete}
                                            onCheckedChange={(value) =>
                                              handleTimeslotToggle(
                                                appt.id!,
                                                slot.id!,
                                                "delete",
                                                value === true,
                                              )
                                            }
                                            disabled={
                                              isCancelSelected ||
                                              isDeleteSelected
                                            }
                                            className="h-3.5 w-3.5"
                                            aria-label="Einheit löschen"
                                          />
                                        </div>
                                      </TableCell>
                                    )}

                                    {/* Delete Besuch (whole appointment) */}
                                    {whoCancelled === "no_trace" &&
                                      idx === 0 && (
                                        <TableCell
                                          rowSpan={timeslots.length}
                                          className={`${CELL_TIGHT} align-top`}
                                        >
                                          <div className="flex justify-center">
                                            <Checkbox
                                              checked={isDeleteSelected}
                                              onCheckedChange={(value) =>
                                                handleAppointmentToggle(
                                                  appt,
                                                  "delete",
                                                  value === true,
                                                )
                                              }
                                              className="h-3.5 w-3.5"
                                              aria-label="Besuch löschen"
                                            />
                                          </div>
                                        </TableCell>
                                      )}
                                  </TableRow>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="px-3 py-6 text-center text-dense text-ink-muted">
                      Keine Termine gefunden.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant="text" disabled={saveLoading}>
            Abbrechen
          </Button>
          <Button
            onClick={handleBatchCancelDeleteClick}
            disabled={
              saveLoading ||
              (appointmentsToCancel.length === 0 &&
                appointmentsToDelete.length === 0 &&
                timeSlotsToCancel.length === 0 &&
                timeSlotsToDelete.length === 0)
            }
            variant="contained"
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default BatchCancelAppointmentDialog;
